import { useRef, useState, type CSSProperties, type HTMLAttributes, type MouseEvent, type ReactNode } from 'react';
import { clsx } from 'clsx';
import { Icon } from './icons';

export interface ListProps extends HTMLAttributes<HTMLDivElement> { variant?: 'plain' | 'inset-grouped' }
export function List({ variant = 'plain', className, ...props }: ListProps) {
  return <div {...props} role={props.role ?? 'list'} className={clsx('hylo-list', `hylo-list--${variant}`, className)} />;
}
export interface ListSectionProps extends HTMLAttributes<HTMLElement> { title?: string; header?: ReactNode; footer?: ReactNode; variant?: 'plain' | 'inset-grouped' }
export function ListSection({ title, header, footer, children, variant = 'inset-grouped', className, ...props }: ListSectionProps) {
  return <section {...props} className={clsx('hylo-list-section', `hylo-list-section--${variant}`, className)} aria-label={props['aria-label'] ?? title}>
    {(header || title) && <div className="hylo-list-section__header">{header ?? title}</div>}
    <div className="hylo-list-section__rows">{children}</div>{footer && <div className="hylo-list-section__footer">{footer}</div>}
  </section>;
}

export interface SwipeAction { label: string; icon?: string; destructive?: boolean; onAction: () => void }
export interface ListRowProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  leading?: ReactNode; title: ReactNode; subtitle?: ReactNode; detail?: ReactNode; accessory?: ReactNode; trailing?: ReactNode;
  chevron?: boolean; selected?: boolean; highlighted?: boolean; destructive?: boolean; disabled?: boolean;
  density?: 'regular' | 'compact';
  onClick?: () => void; href?: string; swipeActions?: readonly SwipeAction[];
}
export function ListRow({ leading, title, subtitle, detail, accessory, trailing, chevron, selected, highlighted, destructive, disabled,
  onClick, href, swipeActions, density = 'regular', className, ...props }: ListRowProps) {
  const [revealed, setRevealed] = useState(false); const start = useRef<{ x: number; y: number } | null>(null);
  const suppressClick = useRef(false);
  const allowActivation = (event: MouseEvent<HTMLElement>) => {
    if (disabled) { event.preventDefault(); return false; }
    if (suppressClick.current) { suppressClick.current = false; event.preventDefault(); return false; }
    if (revealed) { setRevealed(false); event.preventDefault(); return false; }
    return true;
  };
  const activate = (event: MouseEvent<HTMLElement>) => { if (allowActivation(event)) onClick?.(); };
  const primary = <>{leading && <span className="hylo-list-row__leading">{leading}</span>}
    <span className="hylo-list-row__body"><span className="hylo-list-row__heading"><span className="hylo-list-row__title">{title}</span>
      {detail && <span className="hylo-list-row__detail">{detail}</span>}</span>
      {subtitle && <span className="hylo-list-row__subtitle">{subtitle}</span>}</span>
    {(accessory || chevron) && <span className="hylo-list-row__trailing">{accessory}{chevron && <Icon name="chevron.right" className="hylo-chevron" size={14} />}</span>}</>;
  return <div {...props} role={props.role ?? 'listitem'} data-swipe-open={revealed || undefined}
    className={clsx('hylo-list-row', density === 'compact' && 'hylo-list-row--compact', selected && 'is-selected', highlighted && 'is-highlighted', destructive && 'is-destructive', disabled && 'is-disabled', className)}
    onPointerDown={event => {
      props.onPointerDown?.(event);
      suppressClick.current = false;
      if (!disabled && swipeActions?.length && event.pointerType !== 'mouse' && event.currentTarget.closest('[data-layout]')?.getAttribute('data-layout') === 'phone')
        start.current = { x: event.clientX, y: event.clientY };
    }} onPointerUp={event => {
      props.onPointerUp?.(event); const origin = start.current; start.current = null;
      if (!origin || Math.abs(event.clientY - origin.y) > 30) return;
      const rtl = getComputedStyle(event.currentTarget).direction === 'rtl'; const delta = (event.clientX - origin.x) * (rtl ? -1 : 1);
      if (Math.abs(delta) > 45) { suppressClick.current = true; setRevealed(delta < 0); }
    }} onPointerCancel={event => { start.current = null; props.onPointerCancel?.(event); }} onKeyDown={event => {
      props.onKeyDown?.(event); if (event.key === 'Escape') setRevealed(false);
    }}>
    <div className="hylo-list-row__surface">
      {href ? <a role="link" href={disabled ? undefined : href} aria-disabled={disabled || undefined} tabIndex={disabled ? -1 : undefined}
        onClick={activate} onAuxClick={allowActivation} aria-current={selected ? 'true' : undefined} className="hylo-list-row__main">{primary}</a>
        : onClick ? <button type="button" disabled={disabled} onClick={activate}
          aria-current={selected ? 'true' : undefined} className="hylo-list-row__main">{primary}</button>
          : <div className="hylo-list-row__main">{primary}</div>}
      {trailing && <span className="hylo-list-row__trailing">{trailing}</span>}
    </div>
    {swipeActions?.length ? <div className="hylo-list-row__actions" aria-hidden={!revealed}>
      {swipeActions.map((action, i) => <button type="button" key={i} tabIndex={revealed ? 0 : -1} disabled={disabled}
        className={action.destructive ? 'is-destructive' : undefined} onClick={() => { setRevealed(false); action.onAction(); }}>
        {action.icon && <Icon name={action.icon} />}{action.label}</button>)}
    </div> : null}
  </div>;
}

export interface SettingsRowProps extends Omit<ListRowProps, 'leading' | 'accessory' | 'trailing' | 'subtitle'> {
  icon: string; tint?: string; value?: ReactNode; control?: ReactNode; subtitle?: ReactNode;
}
export interface IconTileProps { name: string; tint?: string; size?: number; className?: string }
/** HyloUI SettingsIconTile: decorative, tinted tile; use 36px for native file rows. */
export function IconTile({ name, tint = 'var(--hylo-color-accent)', size, className }: IconTileProps) {
  return <span aria-hidden="true" className={clsx('hylo-settings-icon', className)}
    style={{ '--settings-tint': tint, '--icon-tile-base': size === undefined ? 'var(--hylo-metric-settings-icon)' : `${size}px` } as CSSProperties}>
    <Icon name={name} />
  </span>;
}
export function SettingsRow({ icon, tint = 'var(--hylo-color-accent)', value, control, chevron, onClick, ...props }: SettingsRowProps) {
  return <ListRow {...props} onClick={control ? undefined : onClick} chevron={chevron ?? (!!onClick && !control && !props.destructive)}
    className={clsx('hylo-settings-row', props.className)}
    leading={<IconTile name={icon} tint={tint} />}
    accessory={!control && value !== undefined ? <span className="hylo-settings-row__value">{value}</span> : undefined} trailing={control} />;
}

export function Divider({ className, ...props }: HTMLAttributes<HTMLHRElement>) {
  return <hr {...props} className={clsx('hylo-divider', className)} />;
}
export interface CardProps extends HTMLAttributes<HTMLDivElement> { padding?: 'none' | 'small' | 'regular' }
export function Card({ padding = 'regular', className, ...props }: CardProps) {
  return <div {...props} className={clsx('hylo-card', `hylo-card--${padding}`, className)} />;
}
export interface DisclosureGroupProps extends Omit<HTMLAttributes<HTMLDetailsElement>, 'title' | 'onChange'> {
  title: ReactNode; leading?: ReactNode; open?: boolean; onOpenChange?: (open: boolean) => void;
}
export function DisclosureGroup({ title, leading, children, onOpenChange, className, ...props }: DisclosureGroupProps) {
  return <details {...props} className={clsx('hylo-disclosure', className)} onToggle={event => onOpenChange?.(event.currentTarget.open)}>
    <summary>{leading}<span>{title}</span><Icon name="chevron.right" className="hylo-chevron" size={14} /></summary>
    <div className="hylo-disclosure__content">{children}</div></details>;
}
